import React, { useEffect, useMemo, useRef, useState } from "react";
import { useLazyGetAiAccountReviewQuery, useTriggerAiAccountReviewMutation } from "../../../../../../../../../../redux/apiSlice";

const normalizeText = (value) => (value === null || value === undefined ? "" : `${value}`);

const extractJsonCodeBlock = (text) => {
  const raw = normalizeText(text);
  const re = /```(?:json)?\s*\n([\s\S]*?)\n```/i;
  const match = raw.match(re);
  if (!match) return { without: raw, json: "" };
  const json = match[1] || "";
  const without = raw.replace(match[0], "").trim();
  return { without, json };
};

const stampPayload = (payload) => {
  if (!payload || typeof payload !== "object") return payload;
  return { ...payload, __ts: Date.now() };
};

const parseAiSummary = (text) => {
  const { without, json } = extractJsonCodeBlock(text);
  const lines = normalizeText(without).split(/\r?\n/);

  const sections = {
    summary: [],
    anomalies: [],
    gaps: [],
    recommendation: [],
    other: [],
  };

  let current = "other";
  for (const line of lines) {
    const trimmed = line.trim();
    if (!trimmed) continue;

    const heading = trimmed.match(/^###\s+(.+)$/i);
    if (heading) {
      const name = heading[1].trim().toLowerCase();
      if (name.startsWith("summary")) current = "summary";
      else if (name.startsWith("anomal")) current = "anomalies";
      else if (name.startsWith("gap")) current = "gaps";
      else if (name.startsWith("recommend")) current = "recommendation";
      else current = "other";
      continue;
    }

    sections[current].push(trimmed);
  }

  const toBullets = (entries) =>
    entries
      .map((l) => l.replace(/^\s*(?:-|\*|\d+\.)\s+/, "").trim())
      .filter(Boolean);

  const summary = sections.summary.join("\n").trim() || "";
  const anomalies = toBullets(sections.anomalies);
  const gaps = toBullets(sections.gaps);
  const recommendationRaw = sections.recommendation.join("\n").trim() || "";
  const other = sections.other.join("\n").trim() || "";

  const recommendationLabel =
    recommendationRaw.match(/\*\*([^*]+)\*\*/)?.[1]?.trim() ||
    recommendationRaw.split(":")[0]?.trim() ||
    "";

  const normalizedRecommendation = recommendationLabel.toLowerCase();
  const recommendationVariant =
    normalizedRecommendation.includes("escalat") ? "danger" :
    normalizedRecommendation.includes("monitor") ? "warning" :
    normalizedRecommendation.includes("review") ? "warning" :
    normalizedRecommendation.includes("approve") ? "success" :
    normalizedRecommendation.includes("safe") ? "success" :
    "primary";

  return {
    summary,
    anomalies,
    gaps,
    recommendationRaw,
    recommendationLabel,
    recommendationVariant,
    other,
    json,
  };
};

const stripInlineMarkdown = (value) =>
  normalizeText(value)
    .replace(/\*\*([^*]+)\*\*/g, "$1")
    .replace(/`([^`]+)`/g, "$1");

const renderAiText = (text, { muted = false } = {}) => {
  const raw = normalizeText(text);
  if (!raw.trim()) {
    return <div className={muted ? "text-muted" : "text-dark-75"}>-</div>;
  }

  const lines = raw.split(/\r?\n/);
  const nodes = [];
  let listItems = [];
  let keyIndex = 0;

  const flushList = () => {
    if (!listItems.length) return;
    nodes.push(
      <ul key={`list-${keyIndex++}`} className={`pl-5 mb-3 ${muted ? "text-muted" : "text-dark-75"}`}>
        {listItems.map((item, idx) => (
          <li key={`li-${keyIndex++}-${idx}`} style={{ whiteSpace: "pre-wrap" }}>
            {item}
          </li>
        ))}
      </ul>
    );
    listItems = [];
  };

  for (const line of lines) {
    const trimmed = line.trim();
    if (!trimmed) {
      flushList();
      nodes.push(<div key={`sp-${keyIndex++}`} style={{ height: 10 }} />);
      continue;
    }

    const heading =
      trimmed.match(/^#{1,6}\s+(.+)$/) ||
      trimmed.match(/^\*\*([^*]+)\*\*:?\s*$/);
    if (heading) {
      flushList();
      const label = stripInlineMarkdown(heading[1] || heading[0]);
      nodes.push(
        <div
          key={`h-${keyIndex++}`}
          className={`font-weight-bolder mt-3 mb-2 ${muted ? "text-muted" : "text-dark-75"}`}
        >
          {label}
        </div>
      );
      continue;
    }

    const bullet = trimmed.match(/^(?:-|\*|\d+\.)\s+(.+)$/);
    if (bullet) {
      listItems.push(stripInlineMarkdown(bullet[1]));
      continue;
    }

    flushList();
    nodes.push(
      <div key={`p-${keyIndex++}`} className={muted ? "text-muted" : "text-dark-75"} style={{ whiteSpace: "pre-wrap" }}>
        {stripInlineMarkdown(trimmed)}
      </div>
    );
  }

  flushList();
  return <div>{nodes}</div>;
};

const AiAccountReviewBody = ({ userId, accountId }) => {
  const [activeJobId, setActiveJobId] = useState(null);
  const [analysisRequested, setAnalysisRequested] = useState(false);
  const [triggerMeta, setTriggerMeta] = useState(null);
  const [uiError, setUiError] = useState("");
  const [analysisData, setAnalysisData] = useState(null);
  const [analysisError, setAnalysisError] = useState("");
  const [isPolling, setIsPolling] = useState(false);
  const [analysisStartedAt, setAnalysisStartedAt] = useState(null);
  const [nowMs, setNowMs] = useState(Date.now());
  const [isRetrying, setIsRetrying] = useState(false);
  const [showRaw, setShowRaw] = useState(false);
  const [showSteps, setShowSteps] = useState(false);
  const [showJson, setShowJson] = useState(false);
  const triggerPromiseRef = useRef(null);
  const analysisPromiseRef = useRef(null);
  const pollingTimerRef = useRef(null);
  const pollingAttemptsRef = useRef(0);
  const didTriggerRecheckRef = useRef(false);

  useEffect(() => {
    setActiveJobId(null);
    setAnalysisRequested(false);
    setTriggerMeta(null);
    setUiError("");
    setAnalysisData(null);
    setAnalysisError("");
    setIsPolling(false);
    setAnalysisStartedAt(null);
    setIsRetrying(false);
    setShowRaw(false);
    setShowSteps(false);
    setShowJson(false);
    pollingAttemptsRef.current = 0;
    didTriggerRecheckRef.current = false;
    if (pollingTimerRef.current) {
      clearTimeout(pollingTimerRef.current);
      pollingTimerRef.current = null;
    }
  }, [accountId]);

  useEffect(() => {
    return () => {
      if (analysisPromiseRef.current?.abort) analysisPromiseRef.current.abort();
      if (triggerPromiseRef.current?.abort) triggerPromiseRef.current.abort();
      if (pollingTimerRef.current) {
        clearTimeout(pollingTimerRef.current);
        pollingTimerRef.current = null;
      }
    };
  }, []);

  const [triggerReview, { isLoading: isTriggeringReview, error: triggerError }] = useTriggerAiAccountReviewMutation();

  const [fetchReview, { isFetching: isFetchingReview, error: reviewError }] = useLazyGetAiAccountReviewQuery();

  const fetchAndStoreReview = async (jobId) => {
    const promise = fetchReview({ jobId }, false);
    analysisPromiseRef.current = promise;
    try {
      const resp = await promise.unwrap();
      setAnalysisData(stampPayload(resp));
      return resp;
    } finally {
      analysisPromiseRef.current = null;
    }
  };

  const recheckViaTrigger = async () => {
    const promise = triggerReview({ userId, accountId });
    triggerPromiseRef.current = promise;
    try {
      const resp = await promise.unwrap();
      const payload = resp?.data ?? resp;
      const inner = payload?.data ?? payload;
      const jobId = inner?.jobId ?? payload?.jobId;
      const status = inner?.status ?? payload?.status;
      const cachedResult = inner?.result ?? payload?.result;
      const cachedSummary = cachedResult?.summary;

      if (cachedSummary) {
        setTriggerMeta({ jobId: null, status: "completed" });
        setAnalysisRequested(true);
        setAnalysisData(stampPayload(inner));
        setIsPolling(false);
        return { cached: true };
      }

      if (jobId && jobId !== activeJobId) {
        setActiveJobId(jobId);
        setTriggerMeta({ jobId, status });
        pollingAttemptsRef.current = 0;
        didTriggerRecheckRef.current = false;
        return { cached: false, switchedJob: true };
      }

      return { cached: false, switchedJob: false };
    } finally {
      triggerPromiseRef.current = null;
    }
  };

  const normalizedReviewData = useMemo(() => {
    if (!analysisData) return null;
    const top = analysisData?.data ?? analysisData;
    const inner = top?.data ?? top;
    return inner && typeof inner === "object" ? inner : null;
  }, [analysisData]);

  const reviewStatusRaw = normalizedReviewData?.status;
  const reviewStatus = typeof reviewStatusRaw === "string" ? reviewStatusRaw.toLowerCase() : reviewStatusRaw;
  const reviewResult = normalizedReviewData?.result;
  const reviewSummary = reviewResult?.summary || "";

  const reviewSteps = useMemo(() => {
    const steps = reviewResult?._agentPlan?.steps;
    return Array.isArray(steps) ? steps : [];
  }, [reviewResult]);

  const runReview = async () => {
    setUiError("");
    setAnalysisRequested(false);
    setAnalysisData(null);
    setAnalysisError("");
    didTriggerRecheckRef.current = false;
    setTriggerMeta({ jobId: null, status: "triggering" });
    try {
      const promise = triggerReview({ userId, accountId });
      triggerPromiseRef.current = promise;
      const resp = await promise.unwrap();
      const payload = resp?.data ?? resp;
      const inner = payload?.data ?? payload;
      const jobId = inner?.jobId ?? payload?.jobId;
      const status = inner?.status ?? payload?.status;
      const cachedResult = inner?.result ?? payload?.result;
      const cachedSummary = cachedResult?.summary;

      if (cachedSummary) {
        setTriggerMeta({ jobId: null, status: "completed" });
        setAnalysisRequested(true);
        setAnalysisData(stampPayload(inner));
        return;
      }

      if (jobId) {
        setActiveJobId(jobId);
        setTriggerMeta({ jobId, status });
        return;
      }

      setUiError("Unable to read jobId from trigger response.");
    } catch {
      setUiError("Unable to trigger review.");
    } finally {
      triggerPromiseRef.current = null;
    }
  };

  const runAnalysis = async () => {
    if (!activeJobId) return;
    setAnalysisRequested(true);
    setUiError("");
    setAnalysisError("");
    setIsPolling(true);
    setAnalysisStartedAt(Date.now());
    pollingAttemptsRef.current = 0;
    didTriggerRecheckRef.current = false;
    try {
      await fetchAndStoreReview(activeJobId);
    } catch {
      setAnalysisError("Unable to run analysis.");
    }
  };

  const refreshAnalysis = async () => {
    if (!activeJobId) return;
    setUiError("");
    setAnalysisError("");
    setAnalysisRequested(true);
    try {
      await fetchAndStoreReview(activeJobId);
      if (!hasSummary && !didTriggerRecheckRef.current) {
        didTriggerRecheckRef.current = true;
        try {
          await recheckViaTrigger();
        } catch {
          didTriggerRecheckRef.current = false;
        }
      }
    } catch {
      setAnalysisError("Unable to refresh analysis.");
    }
  };

  const retryAnalysis = async () => {
    setUiError("");
    setAnalysisError("");
    setAnalysisRequested(true);
    setIsRetrying(true);
    setIsPolling(false);
    if (pollingTimerRef.current) {
      clearTimeout(pollingTimerRef.current);
      pollingTimerRef.current = null;
    }
    pollingAttemptsRef.current = 0;
    didTriggerRecheckRef.current = false;
    setAnalysisStartedAt(Date.now());
    try {
      const promise = triggerReview({ userId, accountId });
      triggerPromiseRef.current = promise;
      const resp = await promise.unwrap();
      const payload = resp?.data ?? resp;
      const inner = payload?.data ?? payload;
      const jobId = inner?.jobId ?? payload?.jobId;
      const status = inner?.status ?? payload?.status;
      const cachedResult = inner?.result ?? payload?.result;
      const cachedSummary = cachedResult?.summary;

      if (cachedSummary) {
        setTriggerMeta({ jobId: null, status: "completed" });
        setActiveJobId(null);
        setAnalysisData(stampPayload(inner));
        setIsPolling(false);
        return;
      }

      if (!jobId) {
        setAnalysisError("Unable to retry analysis.");
        return;
      }

      setActiveJobId(jobId);
      setTriggerMeta({ jobId, status });
      setIsPolling(true);
      try {
        await fetchAndStoreReview(jobId);
      } catch {
        setAnalysisError("Unable to run analysis.");
      }
    } catch {
      setAnalysisError("Unable to retry analysis.");
    } finally {
      triggerPromiseRef.current = null;
      setIsRetrying(false);
    }
  };

  const clearReview = () => {
    setAnalysisRequested(false);
    setActiveJobId(null);
    setTriggerMeta(null);
    setUiError("");
    setIsPolling(false);
    setAnalysisStartedAt(null);
    setIsRetrying(false);
    if (analysisPromiseRef.current?.abort) analysisPromiseRef.current.abort();
    if (triggerPromiseRef.current?.abort) triggerPromiseRef.current.abort();
    if (pollingTimerRef.current) {
      clearTimeout(pollingTimerRef.current);
      pollingTimerRef.current = null;
    }
    pollingAttemptsRef.current = 0;
    didTriggerRecheckRef.current = false;
    setAnalysisData(null);
    setAnalysisError("");
    setShowRaw(false);
    setShowSteps(false);
    setShowJson(false);
  };

  const hasSummary = typeof reviewSummary === "string" ? reviewSummary.trim().length > 0 : !!reviewSummary;
  const statusText = activeJobId
    ? (reviewStatusRaw || triggerMeta?.status || "processing")
    : (hasSummary ? (reviewStatusRaw || triggerMeta?.status || "completed") : "-");
  const triggerDisabled = isTriggeringReview || !!activeJobId || hasSummary;
  const analysisDisabled = !activeJobId || isTriggeringReview || isFetchingReview || isPolling || hasSummary;
  const clearDisabled = !(activeJobId || hasSummary || triggerMeta);

  const parsed = useMemo(() => parseAiSummary(reviewSummary), [reviewSummary]);

  const isProcessing = analysisRequested && !hasSummary && (isFetchingReview || isPolling || isRetrying);

  useEffect(() => {
    if (!isProcessing) return;
    const id = setInterval(() => setNowMs(Date.now()), 1000);
    return () => clearInterval(id);
  }, [isProcessing]);

  const processingMs = analysisStartedAt ? Math.max(0, nowMs - analysisStartedAt) : 0;
  const STUCK_AFTER_MS = 10000;
  const showRetryHint = isProcessing && processingMs >= STUCK_AFTER_MS;

  useEffect(() => {
    if (!isPolling) return;
    if (!activeJobId) return;
    if (hasSummary) {
      setIsPolling(false);
      return;
    }

    const status = typeof reviewStatus === "string" ? reviewStatus : "";
    const isTerminal =
      status === "completed" ||
      status === "complete" ||
      status === "done" ||
      status === "success" ||
      status === "succeeded" ||
      status === "failed" ||
      status === "error" ||
      status === "errored" ||
      status === "cancelled" ||
      status === "canceled";

    if (isTerminal) {
      setIsPolling(false);
      return;
    }

    const MAX_ATTEMPTS = 180;
    if (pollingAttemptsRef.current >= MAX_ATTEMPTS) {
      setIsPolling(false);
      return;
    }

    pollingTimerRef.current = setTimeout(async () => {
      pollingAttemptsRef.current += 1;
      try {
        if (pollingAttemptsRef.current >= 6 && !didTriggerRecheckRef.current) {
          didTriggerRecheckRef.current = true;
          try {
            const result = await recheckViaTrigger();
            if (result?.cached || result?.switchedJob) return;
          } catch {
            didTriggerRecheckRef.current = false;
          }
        }
        await fetchAndStoreReview(activeJobId);
      } catch {
        setAnalysisError("Unable to fetch review result.");
      }
    }, 2000);

    return () => {
      if (pollingTimerRef.current) {
        clearTimeout(pollingTimerRef.current);
        pollingTimerRef.current = null;
      }
    };
  }, [activeJobId, hasSummary, isPolling, reviewStatus]);

  return (
    <div className="row">
      <div className="col-lg-12">
        <div className="card card-custom gutter-b">
          <div className="card-header border-0">
            <div className="card-title">
              <h3 className="card-label">
                <span className="d-block text-dark font-weight-bolder">AI Account Review</span>
                <span className="d-block text-muted mt-2 font-size-sm">Runs an AI review for this account</span>
              </h3>
            </div>
            <div className="card-toolbar">
              <button
                type="button"
                className="btn btn-light-primary font-weight-bolder mr-3"
                onClick={runReview}
                disabled={triggerDisabled}
              >
                {activeJobId ? "Triggered" : isTriggeringReview ? "Starting..." : "Trigger Review"}
              </button>
              <button
                type="button"
                className="btn btn-light-success font-weight-bolder mr-3"
                onClick={runAnalysis}
                disabled={analysisDisabled}
              >
                {isFetchingReview || isPolling ? "Running..." : "Run Analysis"}
              </button>
              <button
                type="button"
                className="btn btn-light-danger font-weight-bolder"
                onClick={clearReview}
                disabled={clearDisabled}
              >
                Clear
              </button>
            </div>
          </div>
          <div className="card-body pt-0">
            <div className="d-flex flex-wrap">
              <div className="d-flex flex-column flex-grow-1 mt-0 mb-4 pr-3">
                <span className="text-dark-75 font-weight-bolder font-size-lg">Job ID</span>
                <span className="text-muted font-weight-bold font-size-sm my-1">{activeJobId || "-"}</span>
              </div>
              <div className="d-flex flex-column flex-grow-1 mt-0 mb-4 pr-3">
                <span className="text-dark-75 font-weight-bolder font-size-lg">Status</span>
                <span className="text-muted font-weight-bold font-size-sm my-1">
                  {statusText}
                </span>
              </div>
            </div>

            {!!uiError && (
              <div className="text-danger font-weight-bold mb-4">{uiError}</div>
            )}

            {((activeJobId && !analysisRequested && !hasSummary) || (!activeJobId && !!triggerMeta?.status && triggerMeta.status === "processing")) && (
              <div className="text-muted font-weight-bold mb-4">
                Job created. Click “Run Analysis” to fetch the summary.
              </div>
            )}

            {activeJobId && !uiError && triggerError && (
              <div className="text-danger font-weight-bold mb-4">Unable to trigger review.</div>
            )}

            {activeJobId && reviewError && (
              <div className="text-danger font-weight-bold mb-4">Unable to fetch review result.</div>
            )}

            {activeJobId && analysisError && (
              <div className="text-danger font-weight-bold mb-4">{analysisError}</div>
            )}

            {isProcessing && (
              <div className="text-muted font-weight-bold mb-4">
                Processing...
                {showRetryHint && (
                  <>
                    {" "}
                    <button
                      type="button"
                      className="btn btn-sm btn-light-primary font-weight-bolder ml-2"
                      onClick={retryAnalysis}
                      disabled={isTriggeringReview || isFetchingReview || isRetrying}
                    >
                      {isRetrying ? "Showing..." : "Show Summary"}
                    </button>
                  </>
                )}
              </div>
            )}

            {analysisRequested && !isFetchingReview && normalizedReviewData && !hasSummary && (
              <div className="text-muted font-weight-bold mb-4">Analysis status: {statusText}</div>
            )}

            {hasSummary && (
              <>
                <div className="d-flex flex-wrap align-items-center justify-content-between mb-4">
                  <div className="d-flex align-items-center">
                    <div className="text-dark-75 font-weight-bolder font-size-lg mr-3">AI Output</div>
                  </div>
                  <div className="d-flex align-items-center">
                    {!!parsed.json && (
                      <button
                        type="button"
                        className="btn btn-sm btn-light font-weight-bolder mr-2"
                        onClick={() => setShowJson((v) => !v)}
                      >
                        {showJson ? "Hide JSON" : "Show JSON"}
                      </button>
                    )}
                    <button
                      type="button"
                      className="btn btn-sm btn-light font-weight-bolder"
                      onClick={() => setShowRaw((v) => !v)}
                    >
                      {showRaw ? "Hide Raw" : "Show Raw"}
                    </button>
                  </div>
                </div>

                <div className="row">
                  <div className="col-lg-6">
                    <div
                      className="card card-custom mb-6"
                      style={{ borderLeft: "4px solid #3699FF", background: "#F7F7F8" }}
                    >
                      <div className="card-body">
                        <div className="d-flex align-items-center justify-content-between mb-2">
                          <div className="text-dark-75 font-weight-bolder">Summary</div>
                          <span className="label label-sm label-light-success label-inline">overview</span>
                        </div>
                        <div style={{ fontSize: 14, lineHeight: 1.65 }}>
                          {renderAiText(parsed.summary || reviewSummary || "-")}
                        </div>
                        {!!parsed.other && (
                          <div className="mt-4" style={{ fontSize: 13, lineHeight: 1.6 }}>
                            {renderAiText(parsed.other, { muted: true })}
                          </div>
                        )}
                      </div>
                    </div>
                  </div>

                  <div className="col-lg-6">
                    <div
                      className={`card card-custom mb-6 bg-light-${parsed.recommendationVariant}`}
                      style={{
                        borderLeft:
                          parsed.recommendationVariant === "danger"
                            ? "4px solid #F64E60"
                            : parsed.recommendationVariant === "warning"
                              ? "4px solid #FFA800"
                              : parsed.recommendationVariant === "success"
                                ? "4px solid #1BC5BD"
                                : "4px solid #3699FF",
                      }}
                    >
                      <div className="card-body">
                        <div className="d-flex align-items-center justify-content-between mb-2">
                          <div className="text-dark-75 font-weight-bolder">Recommendation</div>
                        </div>
                        <div style={{ whiteSpace: "pre-wrap" }}>{parsed.recommendationRaw || "-"}</div>
                      </div>
                    </div>
                  </div>

                  <div className="col-lg-6">
                    <div
                      className="card card-custom mb-6 bg-light-danger"
                      style={{ borderLeft: "4px solid #F64E60" }}
                    >
                      <div className="card-body">
                        <div className="d-flex align-items-center justify-content-between mb-2">
                          <div className="text-dark-75 font-weight-bolder">Anomalies</div>
                          <span className="label label-sm label-light-danger label-inline">
                            {parsed.anomalies.length ? `${parsed.anomalies.length}` : "0"}
                          </span>
                        </div>
                        {parsed.anomalies.length ? (
                          <ul className="pl-5 mb-0">
                            {parsed.anomalies.map((item, idx) => (
                              <li key={`anom-${idx}`} style={{ whiteSpace: "pre-wrap" }}>
                                {item}
                              </li>
                            ))}
                          </ul>
                        ) : (
                          <div className="text-muted">No anomalies found.</div>
                        )}
                      </div>
                    </div>
                  </div>

                  <div className="col-lg-6">
                    <div
                      className="card card-custom mb-6 bg-light-warning"
                      style={{ borderLeft: "4px solid #FFA800" }}
                    >
                      <div className="card-body">
                        <div className="d-flex align-items-center justify-content-between mb-2">
                          <div className="text-dark-75 font-weight-bolder">Gaps</div>
                          <span className="label label-sm label-light-warning label-inline">
                            {parsed.gaps.length ? `${parsed.gaps.length}` : "0"}
                          </span>
                        </div>
                        {parsed.gaps.length ? (
                          <ul className="pl-5 mb-0">
                            {parsed.gaps.map((item, idx) => (
                              <li key={`gap-${idx}`} style={{ whiteSpace: "pre-wrap" }}>
                                {item}
                              </li>
                            ))}
                          </ul>
                        ) : (
                          <div className="text-muted">No gaps found.</div>
                        )}
                      </div>
                    </div>
                  </div>
                </div>

                {showJson && !!parsed.json && (
                  <div className="card card-custom mb-6">
                    <div className="card-body">
                      <div className="text-dark-75 font-weight-bolder mb-2">Structured JSON</div>
                      <pre className="mb-0" style={{ whiteSpace: "pre-wrap" }}>{parsed.json}</pre>
                    </div>
                  </div>
                )}

                {showRaw && (
                  <div className="card card-custom mb-6">
                    <div className="card-body">
                      <div className="text-dark-75 font-weight-bolder mb-2">Raw Output</div>
                      <pre className="mb-0" style={{ whiteSpace: "pre-wrap" }}>{reviewSummary}</pre>
                    </div>
                  </div>
                )}

                {!!reviewSteps.length && (
                  <div className="mt-2">
                    <button
                      type="button"
                      className="btn btn-sm btn-light font-weight-bolder mb-4"
                      onClick={() => setShowSteps((v) => !v)}
                    >
                      {showSteps ? "Hide Steps" : `Show Steps (${reviewSteps.length})`}
                    </button>
                    {showSteps && (
                      <ol className="pl-5 mb-0">
                        {reviewSteps.map((s, idx) => (
                          <li key={`${s?.step ?? idx}-${idx}`} style={{ whiteSpace: "pre-wrap" }}>
                            <span className="text-dark font-weight-bold">{s?.action || "-"}</span>
                            {s?.rationale ? <div className="text-muted font-size-sm">{s.rationale}</div> : null}
                          </li>
                        ))}
                      </ol>
                    )}
                  </div>
                )}
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

const AiAccountReview = ({ userId, accountId }) => {
  return (
    <AiAccountReviewBody
      userId={userId}
      accountId={accountId}
    />
  );
};

export default AiAccountReview;
